import { useEffect } from 'react'
import { motion, useSpring, useTransform } from 'framer-motion'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ArrowRight } from 'lucide-react'

function AnimatedNumber({ value }: { value: number }) {
  const spring = useSpring(value, { mass: 0.8, stiffness: 75, damping: 15 })
  const display = useTransform(spring, (current) =>
    current.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
  )

  useEffect(() => {
    spring.set(value)
  }, [value, spring])

  return <motion.span>{display}</motion.span>
}

export interface WalletCardProps {
  balance: number
  subscriptionValue: number
  isSubscriber: boolean
  onRedeem: () => void
  isLoading?: boolean
}

export function WalletCard({
  balance,
  subscriptionValue,
  isSubscriber,
  onRedeem,
  isLoading = false,
}: WalletCardProps) {
  const isEligible = balance > 0
  const coversFullSubscription = isSubscriber && balance >= subscriptionValue

  return (
    <Card className="w-full max-w-md mx-auto border shadow-sm">
      <CardHeader className="pb-2 space-y-3">
        <div className="flex justify-between items-start">
          <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
            Saldo Disponível
          </CardTitle>
          {coversFullSubscription && (
            <Badge
              variant="secondary"
              className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 px-2 font-medium"
            >
              1 Mês Grátis
            </Badge>
          )}
        </div>
        <div className="flex items-baseline gap-1">
          <span className="text-4xl font-semibold tracking-tight">
            <AnimatedNumber value={balance} />
          </span>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {isSubscriber && (
          <div className="space-y-4">
            <div className="flex justify-between items-center text-sm border-b pb-3">
              <span className="text-muted-foreground">Assinatura Mensal</span>
              <span className="font-medium">
                {subscriptionValue.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
              </span>
            </div>

            {balance < subscriptionValue && balance > 0 && (
              <div className="space-y-2">
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>Progresso</span>
                  <span>{Math.round((balance / subscriptionValue) * 100)}%</span>
                </div>
                <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
                  <motion.div
                    className="h-full bg-primary"
                    initial={{ width: 0 }}
                    animate={{ width: `${(balance / subscriptionValue) * 100}%` }}
                    transition={{ duration: 0.8, ease: 'easeOut' }}
                  />
                </div>
                <p className="text-xs text-muted-foreground pt-1">
                  Falta{' '}
                  {(subscriptionValue - balance).toLocaleString('pt-BR', {
                    style: 'currency',
                    currency: 'BRL',
                  })}{' '}
                  para a próxima mensalidade gratuita.
                </p>
              </div>
            )}
          </div>
        )}

        <div className="pt-2">
          {isEligible ? (
            <Button
              className="w-full h-11 text-base font-medium transition-all"
              onClick={onRedeem}
              loading={isLoading}
            >
              {isLoading ? (
                'Processando...'
              ) : (
                <>
                  Usar Saldo
                  <ArrowRight className="ml-2 h-4 w-4 opacity-70" />
                </>
              )}
            </Button>
          ) : (
            <Button
              variant="outline"
              className="w-full h-11 border-dashed"
              onClick={() => {
                /* copy link logic */
              }}
            >
              Começar a Indicar
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
